import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";

// How many Recent Activity entries a manager has not seen in one league, and a
// function to mark them all seen. Backs the badge on the Recent Activity pill.
//
// "Seen" is league_members.activity_seen_at (migration 070), so it follows the
// manager across devices. Every event type counts: roster moves, waiver wins,
// freezes, 5x Plays and smack talk. The manager's own entries do not, and nor
// does smack talk from someone they have blocked, which the feed hides.
//
// Rows rather than a head count, capped at COUNT_CAP: the badge stops at 9+,
// so reading more than that is waste, and the blocked smack talk rule needs the
// event type and author. There is no realtime publication for move_history, so
// the count refreshes on mount and when the app returns to the foreground.
const COUNT_CAP = 10;

export function useActivityUnread({ leagueId, memberId, members }) {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!leagueId || !memberId) return;

    const { data: me, error: meError } = await supabase
      .from("league_members")
      .select("activity_seen_at")
      .eq("id", memberId)
      .maybeSingle();
    // Before migration 070 the column does not exist. No badge, no noise.
    if (meError || !me?.activity_seen_at) return;

    const { data: rows, error } = await supabase
      .from("move_history")
      .select("member_id, event_type")
      .eq("league_id", leagueId)
      .neq("member_id", memberId)
      .gt("created_at", me.activity_seen_at)
      .order("created_at", { ascending: false })
      .limit(COUNT_CAP + 5);
    if (error) {
      console.error("Error counting unread activity:", error);
      return;
    }

    const blockedMembers = new Set((members || []).filter((m) => m.blocked).map((m) => m.id));
    const visible = (rows || []).filter(
      (r) => !(r.event_type === "smack_talk" && blockedMembers.has(r.member_id))
    );
    setCount(visible.length);
  }, [leagueId, memberId, members]);

  useEffect(() => {
    refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  // Clears the badge straight away rather than after the write, so opening
  // the section never shows a stale number. A failed write only means the
  // count comes back on the next refresh.
  const markSeen = useCallback(async () => {
    setCount(0);
    if (!memberId) return;
    const { error } = await supabase
      .from("league_members")
      .update({ activity_seen_at: new Date().toISOString() })
      .eq("id", memberId);
    if (error) console.error("Error marking activity seen:", error);
  }, [memberId]);

  return { count, markSeen };
}
